import React from 'react';
import { Button, HeadingText30, renderMessage } from '@modernhealth/vital-components';
import { captureException } from '@sentry/browser';
import { SessionListItem } from '@modernhealth/circles-web-components';
import {
    CircleTopicAuthenticatedFragment,
    useCreateAuthenticatedRsvpMutation,
} from '@modernhealth/circles-graphql/v2';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import { InternalLinkButton } from 'src/shared/components/InternalLinkButton';

import { dateAddMinutes } from '../pages/CirclesTopicPage';
import { routes } from '../CirclesRouter';
import { getErrorMessage } from '../utils/getErrorMessage';

interface RsvpButtonProps {
    startTime: string;
    providerName: string;
    topicSlug: string;
    sessionId: string;
}

function useDescribedDate(startTime: string) {
    const intl = useIntl();

    return intl.formatDate(startTime, {
        day: 'numeric',
        month: 'long',
        hour: 'numeric',
        minute: 'numeric',
        timeZoneName: 'long',
    });
}

const RsvpRegisterButton = ({ startTime, providerName, topicSlug, sessionId }: RsvpButtonProps) => {
    const describedDate = useDescribedDate(startTime);
    const { t } = useTranslation();
    const history = useHistory();
    const [createRsvp] = useCreateAuthenticatedRsvpMutation();

    const addRsvp = async sessionId => {
        const { data } = await createRsvp({
            variables: {
                data: {
                    sessionId,
                    rsvpQuestionAnswers: [],
                },
            },
        });

        const rsvp = data?.circleCreateAuthenticatedRsvp;

        if (rsvp && 'rsvpId' in rsvp) {
            history.push(routes.topic(topicSlug).registered(rsvp.rsvpId).confirmation);
        } else {
            const errorType = rsvp?.__typename;

            renderMessage({
                content: t(getErrorMessage(errorType)),
                type: 'error',
                duration: 5,
            });
        }
    };

    const onRsvpButtonClick = () => {
        addRsvp(sessionId).catch(error => {
            renderMessage({
                content: t(getErrorMessage()),
                type: 'error',
                duration: 5,
            });

            captureException(error);
        });
    };

    return (
        <Button
            aria-label={t('circles:pages.topic.register.aria_action', {
                date: describedDate,
                provider_name: providerName ?? '',
            })}
            variant="primary"
            onClick={onRsvpButtonClick}
        >
            Register
        </Button>
    );
};

const RsvpFormLinkButton = ({ startTime, providerName, topicSlug, sessionId }: RsvpButtonProps) => {
    const describedDate = useDescribedDate(startTime);
    const { t } = useTranslation();

    return (
        <InternalLinkButton
            aria-label={t('circles:pages.topic.registration_form.aria_description', {
                date: describedDate,
                provider_name: providerName ?? '',
            })}
            variant="primary"
            to={routes.topic(topicSlug).session(sessionId).register}
        >
            Register
        </InternalLinkButton>
    );
};

export const CircleSessionItemList: React.FC<{
    topic: CircleTopicAuthenticatedFragment;
    topicSlug: string;
    useRsvpPage: boolean;
}> = ({ topic, topicSlug, useRsvpPage }) => {
    const { duration, activeRsvps } = topic;
    const anySessionsRegistered = (activeRsvps && activeRsvps.length > 0) ?? false;
    const { t } = useTranslation();
    const intl = useIntl();

    return (
        <>
            <HeadingText30 marginTop={48} marginBottom={30}>
                {anySessionsRegistered
                    ? t('circles:pages.topic.available_sessions.header')
                    : t('circles:pages.topic.sessions.header')}
            </HeadingText30>
            {topic.sessions.edges.map(session => {
                if (!session?.node) {
                    return null;
                }

                const providersNames = session.node.facilitators.map(
                    provider => provider?.name ?? '',
                );

                const hostsNamesForAriaLabel = intl.formatList(
                    session.node.facilitators.map(provider => provider?.name ?? ''),
                );

                // If an RSVP has additional questions, use a link button to navigate them
                // to the registration form. Otherwise show a normal button which will
                // trigger the RSVP mutation in one click.
                const RsvpButton = useRsvpPage ? RsvpFormLinkButton : RsvpRegisterButton;
                const sessionEndTime = dateAddMinutes(session.node.startTime, duration);
                return (
                    <SessionListItem
                        key={session.node.id}
                        providersNames={providersNames}
                        startTime={session.node.startTime}
                        endTime={sessionEndTime.toISOString()}
                        bookingStatus={session.node.bookingStatus}
                        primaryButton={
                            <RsvpButton
                                startTime={session.node.startTime}
                                providerName={hostsNamesForAriaLabel}
                                topicSlug={topicSlug}
                                sessionId={session.node.id}
                            />
                        }
                    />
                );
            })}
        </>
    );
};
